import React, { useState } from 'react';
import { DatePicker, Empty, Spin, Switch, Typography } from 'antd';
import dayjs from 'dayjs';
import { useBoxDay } from '../../hooks/useStudioOps';
import styles from './index.module.css';

function boxNumber(sequence) {
    if (sequence == null || sequence === '') return '—';
    return String(sequence).padStart(2, '0');
}

function formatWhen(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
        timeZone: 'Europe/Berlin',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
    });
}

function place(box) {
    return [box.companyCode, box.studioCode, box.destination].filter(Boolean).join(' · ') || '—';
}

function person(box) {
    if (box.by && box.email && box.by !== box.email) return box.by;
    return box.by || box.email || '—';
}

function personTitle(box) {
    if (box.by && box.email && box.by !== box.email) return `${box.by} (${box.email})`;
    return person(box);
}

function BoxCard({ box, tone }) {
    return (
        <article className={`${styles.card} ${tone === 'audit' ? styles.auditCard : styles.outboundCard}`}>
            <div className={styles.cardTop}>
                <div>
                    <div className={styles.kicker}>Box</div>
                    <div className={styles.number}>{boxNumber(box.sequence)}</div>
                </div>
                <div className={styles.items}>{box.items ?? 0} items</div>
            </div>
            <div className={styles.meta}>{place(box)}</div>
            <div className={styles.boxId}>{box.boxId}</div>
            <div className={styles.foot}>
                <span>{formatWhen(box.at)}</span>
                <span className={styles.person} title={personTitle(box)}>
                    {person(box)}
                </span>
            </div>
        </article>
    );
}

function BoxSection({ title, hint, tone, boxes }) {
    return (
        <section>
            <div className={styles.sectionHead}>
                <h2>{title}</h2>
                <span>{hint}</span>
            </div>
            {boxes.length ? (
                <div className={styles.grid}>
                    {boxes.map(box => (
                        <BoxCard key={box.id || box.boxId} box={box} tone={tone} />
                    ))}
                </div>
            ) : (
                <Typography.Text type="secondary" className={styles.empty}>
                    None this day
                </Typography.Text>
            )}
        </section>
    );
}

export default function LiveBoxes() {
    const [selectedDate, setSelectedDate] = useState(null);
    const [autoReload, setAutoReload] = useState(false);
    const { data, error, isFetching } = useBoxDay(selectedDate, autoReload);
    const day = data?.data || {};
    const outbound = day.outbound || { total: 0, boxes: [] };
    const audit = day.audit || { total: 0, boxes: [] };

    return (
        <div className={styles.page}>
            <div className={styles.toolbar}>
                <div className={styles.dateField}>
                    <label>Date</label>
                    <DatePicker
                        onChange={date => setSelectedDate(date ? date.format('YYYY-MM-DD') : null)}
                        style={{ width: 160 }}
                        disabledDate={current => current && current > dayjs().endOf('day')}
                    />
                    <span className={styles.zone}>CET</span>
                </div>
                <div className={styles.stats}>
                    <div className={`${styles.stat} ${styles.outbound}`}>
                        <span className={styles.statValue}>{outbound.total}</span>
                        <span className={styles.statLabel}>Outbound boxes</span>
                    </div>
                    <div className={`${styles.stat} ${styles.audit}`}>
                        <span className={styles.statValue}>{audit.total}</span>
                        <span className={styles.statLabel}>Audit boxes</span>
                    </div>
                </div>
                <div className={styles.reload}>
                    <span>Auto reload</span>
                    <Switch checked={autoReload} onChange={setAutoReload} />
                </div>
            </div>
            <Spin spinning={isFetching}>
                {error ? (
                    <Empty description={error?.response?.data?.message || 'Could not load boxes'} />
                ) : (
                    <>
                        <BoxSection title="Outbound" hint="Shipped this day" tone="outbound" boxes={outbound.boxes} />
                        <BoxSection title="Audit" hint="Passed this day" tone="audit" boxes={audit.boxes} />
                    </>
                )}
            </Spin>
        </div>
    );
}
